// Wave 2B diagnostics — minimal in-app runner.
// Four read-only endpoints (three HQ Notes audits + the R2 TEMP-artwork
// dry-run) fired by a single click.  Displays counts + the R2 rebind
// target only — no full row lists in the UI to keep the page tiny.
// This page is intentionally small so it can stay as a permanent
// admin diagnostic without adding tooling weight.
import { useState } from "react";
import api from "@/lib/api";
import { Loader2, Play, ShieldCheck } from "lucide-react";

const CARDS = [
  {
    key: "orphaned",
    label: "HQ Notes — Orphaned",
    href: "/admin/hq-notes-audit/orphaned",
    describe: (t) =>
      `Scanned ${t.notes_scanned} notes · ${t.orphaned_notes} orphaned`,
  },
  {
    key: "duplicates",
    label: "HQ Notes — Duplicates",
    href: "/admin/hq-notes-audit/duplicates",
    describe: (t) =>
      `Scanned ${t.notes_scanned} notes · ${t.duplicate_groups} duplicate groups · ${t.notes_in_duplicate_groups} notes in groups`,
  },
  {
    key: "drift",
    label: "HQ Notes — Franchisee Drift",
    href: "/admin/hq-notes-audit/franchisee-drift",
    describe: (t) =>
      `Scanned ${t.notes_scanned} notes · ${t.drift_flagged} drift · ${t.sector_has_no_owner} sectors with no owner`,
  },
];

export default function AdminAuditsPage() {
  const [results, setResults] = useState({});
  const [busy, setBusy] = useState({});
  const [error, setError] = useState({});

  const run = async (key, href) => {
    setBusy((b) => ({ ...b, [key]: true }));
    setError((e) => ({ ...e, [key]: null }));
    try {
      const { data } = await api.get(href);
      setResults((r) => ({ ...r, [key]: data }));
    } catch (e) {
      setError((er) => ({
        ...er,
        [key]: e?.response?.data?.detail || e?.message || "Unknown error",
      }));
    } finally {
      setBusy((b) => ({ ...b, [key]: false }));
    }
  };

  const runR2 = async () => {
    setBusy((b) => ({ ...b, r2: true }));
    setError((e) => ({ ...e, r2: null }));
    try {
      const { data } = await api.get(
        "/admin/r2-audit/temp-artwork?franchise_number=0001",
      );
      setResults((r) => ({ ...r, r2: data }));
    } catch (e) {
      setError((er) => ({
        ...er,
        r2: e?.response?.data?.detail || e?.message || "Unknown error",
      }));
    } finally {
      setBusy((b) => ({ ...b, r2: false }));
    }
  };

  const runAll = async () => {
    await Promise.all(CARDS.map((c) => run(c.key, c.href)));
    await runR2();
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="admin-audits-page">
      <header className="flex items-center gap-3 mb-2">
        <ShieldCheck className="w-6 h-6 text-emerald-600" />
        <h1 className="text-2xl font-semibold text-stone-900">Diagnostics</h1>
      </header>
      <p className="text-sm text-stone-600 mb-8">
        Read-only audits.  Every call returns{" "}
        <code className="bg-stone-100 px-1 rounded">write_performed: false</code>{" "}
        — nothing on HQ Notes / Franchisee data is written, deleted, merged
        or moved.
      </p>

      <button
        onClick={runAll}
        className="mb-8 inline-flex items-center gap-2 px-4 py-2 bg-stone-900 text-white rounded-lg text-sm hover:bg-stone-800"
        data-testid="run-all-audits-btn"
      >
        <Play className="w-4 h-4" /> Run all four
      </button>

      <div className="space-y-4">
        {CARDS.map((c) => {
          const r = results[c.key];
          const isBusy = !!busy[c.key];
          const err = error[c.key];
          return (
            <div
              key={c.key}
              className="rounded-xl border border-stone-200 bg-white p-5"
              data-testid={`audit-card-${c.key}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="font-medium text-stone-900">{c.label}</div>
                <button
                  onClick={() => run(c.key, c.href)}
                  disabled={isBusy}
                  className="inline-flex items-center gap-2 px-3 py-1.5 border border-stone-300 rounded-md text-xs hover:bg-stone-50 disabled:opacity-50"
                  data-testid={`run-${c.key}-btn`}
                >
                  {isBusy ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Play className="w-3.5 h-3.5" />
                  )}
                  Run
                </button>
              </div>
              {err && (
                <div
                  className="mt-3 text-xs text-red-700 bg-red-50 rounded p-2"
                  data-testid={`audit-error-${c.key}`}
                >
                  {err}
                </div>
              )}
              {r && (
                <div
                  className="mt-3 text-sm text-stone-700"
                  data-testid={`audit-result-${c.key}`}
                >
                  <div>{c.describe(r.totals)}</div>
                  <div className="mt-1 text-xs text-stone-500">
                    write_performed: {String(r.write_performed)} · audit_version:{" "}
                    {r.audit_version}
                  </div>
                  {/* Duplicate group inline details — read-only, so
                      Sam can eyeball genuine-vs-legitimate. Only
                      renders for the duplicates card. */}
                  {c.key === "duplicates" && (r.duplicate_groups || []).length > 0 && (
                    <div className="mt-3 space-y-3" data-testid="duplicate-details">
                      {(r.duplicate_groups || []).map((g, gi) => (
                        <div
                          key={gi}
                          className="rounded border border-amber-200 bg-amber-50 p-3 text-xs"
                        >
                          <div className="font-mono text-stone-600">
                            franchisee_id: {g.franchisee_id}
                          </div>
                          <div className="font-mono text-stone-600">
                            distinct home_ids: {g.distinct_home_ids.join(", ")}
                          </div>
                          <div className="mt-2 space-y-2">
                            {(g.notes || []).map((n, ni) => (
                              <div
                                key={ni}
                                className="rounded bg-white border border-stone-200 p-2"
                              >
                                <div className="font-mono text-stone-500">
                                  home_id: {n.home_id} · source: {n.source} · id: {n.id}
                                </div>
                                <div className="font-mono text-stone-500">
                                  updated_by_name: {n.updated_by_name || "—"} · updated_at:{" "}
                                  {n.updated_at || "—"}
                                </div>
                                <div className="mt-1 whitespace-pre-wrap text-stone-800">
                                  {n.note}
                                </div>
                              </div>
                            ))}
                          </div>
                          {Object.keys(g.sites_by_home_id || {}).length > 0 && (
                            <div className="mt-2 font-mono text-stone-500">
                              sites:{" "}
                              {Object.entries(g.sites_by_home_id)
                                .map(([hid, site]) => `${hid} → ${site.name || "?"}`)
                                .join(" | ")}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* R2 TEMP artwork dry-run */}
        <div
          className="rounded-xl border border-stone-200 bg-white p-5"
          data-testid="audit-card-r2"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="font-medium text-stone-900">
              R2 TEMP Artwork — 0001 Dry-Run
            </div>
            <button
              onClick={runR2}
              disabled={!!busy.r2}
              className="inline-flex items-center gap-2 px-3 py-1.5 border border-stone-300 rounded-md text-xs hover:bg-stone-50 disabled:opacity-50"
              data-testid="run-r2-btn"
            >
              {busy.r2 ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Play className="w-3.5 h-3.5" />
              )}
              Run
            </button>
          </div>
          {error.r2 && (
            <div
              className="mt-3 text-xs text-red-700 bg-red-50 rounded p-2"
              data-testid="audit-error-r2"
            >
              {error.r2}
            </div>
          )}
          {results.r2 && (
            <div className="mt-3 text-sm text-stone-700" data-testid="audit-result-r2">
              <div>
                Files matched: {results.r2.totals.files_matched} · Rebind
                recommendations: {results.r2.totals.rebind_recommendations} ·
                Ambiguous: {results.r2.totals.ambiguous_destinations}
              </div>
              {(results.r2.files || []).map((f, i) => (
                <div
                  key={i}
                  className="mt-3 rounded border border-stone-200 bg-stone-50 p-3 text-xs font-mono"
                  data-testid={`audit-r2-file-${i}`}
                >
                  <div>key: {f.file.key}</div>
                  <div>
                    currently_bound_id: {f.binding_diff.currently_bound_id || "—"}
                  </div>
                  <div>
                    deduced_number: {f.deduced_franchise_number || "—"}
                  </div>
                  <div>action: {f.recommendation.action}</div>
                  <div>
                    proposed franchisee:{" "}
                    {f.recommendation?.target?.organisation ||
                      f.recommendation?.target?.first_name ||
                      "—"}{" "}
                    (id: {f.recommendation?.target?.id || "—"})
                  </div>
                </div>
              ))}
              <div className="mt-2 text-xs text-stone-500">
                write_performed: {String(results.r2.write_performed)} · audit_version:{" "}
                {results.r2.audit_version}
              </div>
            </div>
          )}
        </div>
        {/* Marketing image migration — dry-run only from this UI.
            The full ``apply: true`` run stays as a curl call so we
            never accidentally trigger the R2 copies from a stray
            click. */}
        <div
          className="rounded-xl border border-stone-200 bg-white p-5"
          data-testid="audit-card-marketing"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="font-medium text-stone-900">
              Marketing Images — Dry-Run Migration
            </div>
            <button
              onClick={async () => {
                setBusy((b) => ({ ...b, marketing: true }));
                setError((e) => ({ ...e, marketing: null }));
                try {
                  const { data } = await api.post(
                    "/admin/marketing-images/migrate-to-artwork",
                    {},
                  );
                  setResults((r) => ({ ...r, marketing: data }));
                } catch (e) {
                  setError((er) => ({
                    ...er,
                    marketing:
                      e?.response?.data?.detail || e?.message || "Unknown error",
                  }));
                } finally {
                  setBusy((b) => ({ ...b, marketing: false }));
                }
              }}
              disabled={!!busy.marketing}
              className="inline-flex items-center gap-2 px-3 py-1.5 border border-stone-300 rounded-md text-xs hover:bg-stone-50 disabled:opacity-50"
              data-testid="run-marketing-btn"
            >
              {busy.marketing ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Play className="w-3.5 h-3.5" />
              )}
              Dry-Run
            </button>
          </div>
          {error.marketing && (
            <div
              className="mt-3 text-xs text-red-700 bg-red-50 rounded p-2"
              data-testid="audit-error-marketing"
            >
              {error.marketing}
            </div>
          )}
          {results.marketing && (
            <div
              className="mt-3 text-sm text-stone-700"
              data-testid="audit-result-marketing"
            >
              <div>
                apply: <span className="font-mono">{String(results.marketing.apply)}</span>
              </div>
              <div className="mt-1">
                Rows examined: {results.marketing.totals.rows_examined} · Would copy:{" "}
                {results.marketing.totals.would_copy} · Skipped (no franchisee):{" "}
                {results.marketing.totals.skipped_no_franchisee} · Skipped (source
                missing): {results.marketing.totals.skipped_missing_source} · Errors:{" "}
                {results.marketing.totals.errors}
              </div>
              {(results.marketing.errors || []).length > 0 && (
                <div className="mt-2 text-xs text-red-700 bg-red-50 rounded p-2 font-mono whitespace-pre-wrap">
                  {results.marketing.errors.slice(0, 5).join("\n")}
                </div>
              )}
              <div className="mt-3 space-y-2">
                {(results.marketing.report || []).slice(0, 6).map((r, i) => (
                  <div
                    key={i}
                    className="rounded border border-stone-200 bg-stone-50 p-2 text-[11px] font-mono"
                    data-testid={`audit-marketing-row-${i}`}
                  >
                    <div>action: {r.action}{r.note ? ` · ${r.note}` : ""}</div>
                    <div className="truncate">from: {r.files_index_key}</div>
                    <div className="truncate">→ to: {r.new_key || "—"}</div>
                  </div>
                ))}
                {(results.marketing.report || []).length > 6 && (
                  <div className="text-[11px] text-stone-500">
                    …and {results.marketing.report.length - 6} more
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
